import {
    Button,
    Form,
    Headline,
    InputControl,
    Loading,
    RequiredText,
    Spacer,
    StyleFunction,
    Text,
    TextInput,
    useThemedStyle,
} from "@venuepos/react-common";
import { GQFiskalyOrganizationInput } from "graphql-sdk";
import { useTranslation } from "locales";
import React, { View } from "react-native";
import { Divider } from "react-native-paper";

export function FiskalyOrganizationForm({
    form,
    onSubmit,
    onGoLive,
}: {
    form: Form<GQFiskalyOrganizationInput>;
    onSubmit: () => void;
    onGoLive: () => Promise<void>;
}) {
    const [t] = useTranslation();
    const styles = useThemedStyle(styleFunc);
    const [{ values, errors }, { setValue, handleSubmit }] = form;

    if (!values) {
        return <Loading />;
    }

    return (
        <View style={styles.container}>
            <InputControl
                error={errors.name}
                description={t(
                    "backoffice.integration.fiskaly.organization_name_description",
                    "The name of the organization"
                )}
            >
                <TextInput
                    value={values.name}
                    onChangeText={newValue => setValue("name", newValue)}
                    label={t("backoffice.common.name", "Name")}
                    required
                />
            </InputControl>
            <InputControl
                error={errors.displayName}
                description={t(
                    "backoffice.integration.fiskaly.organization_display_name_description",
                    "Add an additional display name if needed"
                )}
            >
                <TextInput
                    value={values.displayName}
                    onChangeText={newValue => setValue("displayName", newValue)}
                    label={t(
                        "backoffice.integration.fiskaly.organization_display_name",
                        "Display name"
                    )}
                />
            </InputControl>
            <Spacer />
            <Divider />
            <Spacer />
            <Headline size="h2">
                {t(
                    "backoffice.integration.fiskaly.address_information",
                    "Address information"
                )}
            </Headline>
            <InputControl error={errors.address}>
                <TextInput
                    value={values.address}
                    onChangeText={newValue => setValue("address", newValue)}
                    label={t(
                        "backoffice.integration.fiskaly.address",
                        "Address"
                    )}
                    required
                />
            </InputControl>
            <View style={styles.inputRow}>
                <InputControl error={errors.zip} style={styles.zip}>
                    <TextInput
                        value={values.zip}
                        onChangeText={newValue => setValue("zip", newValue)}
                        label={t("backoffice.common.zip", "Zip")}
                        maxLength={5}
                        required
                    />
                </InputControl>
                <InputControl error={errors.town} style={styles.city}>
                    <TextInput
                        value={values.town}
                        onChangeText={newValue => setValue("town", newValue)}
                        label={t("backoffice.common.city", "City")}
                        required
                    />
                </InputControl>
            </View>
            <InputControl error={errors.state}>
                <TextInput
                    value={values.state}
                    onChangeText={newValue => setValue("state", newValue)}
                    label={t(
                        "backoffice.integration.fiskaly.organization_state",
                        "State"
                    )}
                />
            </InputControl>
            <InputControl error={errors.countryCode}>
                <TextInput
                    disabled={true}
                    value={values.countryCode}
                    label={t(
                        "backoffice.integration.fiskaly.country_code",
                        "Country code"
                    )}
                    required
                />
            </InputControl>
            <Spacer />
            <Divider />
            <Spacer />
            <Headline size="h2">
                {t(
                    "backoffice.integration.fiskaly.tax_information",
                    "Tax information"
                )}
            </Headline>
            <View>
                <Text>
                    {t(
                        "backoffice.integration.fiskaly.tax_information_description",
                        "Only one of these are needed."
                    )}
                </Text>
            </View>
            <Spacer />
            <InputControl
                error={errors.taxNumber}
                description={t(
                    "backoffice.integration.fiskaly.organization_tax_number_description",
                    "The tax number is an ordinal number assigned by the tax office to every taxable natural or legal person"
                )}
            >
                <TextInput
                    value={values.taxNumber}
                    onChangeText={newValue => setValue("taxNumber", newValue)}
                    label={t(
                        "backoffice.integration.fiskaly.organization_tax_number",
                        "Tax number"
                    )}
                />
            </InputControl>
            <InputControl
                error={errors.economyId}
                description={t(
                    "backoffice.integration.fiskaly.organization_economy_id_description",
                    "'Wirtschafts-Identifikationsnummer' for Germany. Used as a clear and permanent identification feature for tax purposes for economically active persons."
                )}
            >
                <TextInput
                    value={values.economyId}
                    onChangeText={newValue => setValue("economyId", newValue)}
                    label={t(
                        "backoffice.integration.fiskaly.organization_economy_id",
                        "Economy id"
                    )}
                />
            </InputControl>
            <InputControl
                error={errors.vatId}
                description={t(
                    "backoffice.integration.fiskaly.organization_vat_id_description",
                    "Identification assigned by the German tax authorities"
                )}
            >
                <TextInput
                    value={values.vatId}
                    onChangeText={newValue => setValue("vatId", newValue)}
                    label={t(
                        "backoffice.integration.fiskaly.organization_vat_id",
                        "VAT ID"
                    )}
                />
            </InputControl>
            <Spacer />
            <Divider />
            <Spacer />
            <View style={styles.inputRow}>
                <Button onPress={handleSubmit(onSubmit)} size="small">
                    {t("backoffice.common.save", "Save")}
                </Button>
                <Button
                    size="small"
                    type="warning"
                    variant="outline"
                    onPress={async () => {
                        await onGoLive();
                    }}
                >
                    {t(
                        "backoffice.integration.fiskaly.go_live_button",
                        "Ready to go live?"
                    )}
                </Button>
            </View>
            <RequiredText />
        </View>
    );
}

const styleFunc: StyleFunction = theme => ({
    container: { maxWidth: theme.dimensions.maxFormWidth },
    inputRow: {
        ...theme.styles.row,
        columnGap: theme.spacingScale,
    },
    zip: {
        ...theme.styles.flex1,
    },
    city: {
        ...theme.styles.flex3,
    },
});
